import type { Dict } from './es';

export const en: Dict = {
  locale: 'en-GB',
  app: {
    name: 'Routecast',
    tagline: 'The weather on every stretch of your route, at the time you drive through it.',
    building: 'Under construction. Meanwhile, have a look at the component gallery.',
    gallery: 'Component gallery',
  },
  risk: {
    levels: ['No risk', 'Caution', 'High', 'Severe'],
    short: ['Low', 'Caution', 'High', 'Severe'],
    unknown: 'No data',
    legend: 'Risk legend',
    legendNote:
      'Indicative thresholds. “Thunderstorm with hail” is an estimated risk, not a certainty. Check official warnings before you set off.',
  },
  factor: {
    rain: 'Rain',
    crosswind: 'Crosswind',
    gust: 'Gusts',
    snow: 'Snow',
    ice: 'Ice',
    visibility: 'Visibility',
    storm: 'Thunderstorm',
  },
  cause: {
    rain: 'Heavy rain',
    crosswind: 'Crosswind',
    gust: 'Wind gusts',
    snow: 'Snow',
    ice: 'Ice',
    visibility: 'Low visibility',
    storm: 'Thunderstorm',
    hail: 'Storm (possible hail)',
  },
  picker: {
    label: 'Departure time',
    days: 'Departure day',
    today: 'Today',
    tomorrow: 'Tomorrow',
    orientative: 'forecast is only a guide',
    orientativeNote: 'Forecast is only a guide (beyond 48 h)',
    now: 'Now',
    nowMark: 'now',
    later: (d) => `+${d}`,
    hint: 'The row picks the day. Drag the dial or use the arrow keys (10 min), Page Up / Page Down (1 h), Home (now) and End (latest).',
    valueText: (when, route, level) => `Departure: ${when}. Route ${route}: ${level}.`,
  },
  roadwind: {
    title: 'Wind relative to the road',
    north: 'N',
    summary: (heading, from, gust, cross) =>
      `Road heading ${heading}°, wind from ${from}°. Of ${gust} km/h gusts, ${cross} km/h push sideways.`,
    calm: 'Calm wind.',
    cross: 'cross',
    head: 'head',
    tail: 'tail',
  },
  compare: {
    caption: 'Routes sorted by risk',
    route: 'Route',
    time: 'Time',
    worst: 'Risk',
    high: 'Km at risk',
    arrival: (t) => `arrive ${t}`,
    noRisk: 'No risky stretches at this time',
    pending: 'Calculating…',
    dataUntil: (km) => `Data up to km ${km}`,
    kmHigh: (km) => `${km} km at high or severe risk`,
  },
  profile: {
    label: (route) => `Risk profile of route ${route}`,
    at: (km, level) => `km ${km}: ${level}`,
    idle: 'Use the arrow keys to move along the route by kilometre.',
    range: (from, to) => `km ${from}–${to}`,
    high: (ranges) => `High or severe risk at ${ranges}.`,
  },
  search: {
    from: 'From',
    to: 'To',
    placeholder: 'City, address or place',
    recent: 'Recent',
    noResults: (q) => `Nothing found for “${q}”. Try the town name.`,
    loading: 'Searching…',
    error: 'Search is not responding. Try again in a few seconds.',
    swap: 'Swap origin and destination',
    clear: 'Clear',
  },
  sheet: {
    handle: 'Routes panel',
    expand: 'Expand panel',
    collapse: 'Collapse panel',
  },
  alerts: {
    title: 'Official warnings',
    count: (n) => (n === 1 ? '1 warning on your route' : `${n} warnings on your route`),
    none: 'No official warnings on your route for that window.',
    notYet: 'Official warnings are not usually issued this far ahead. None listed does not mean a clear day.',
    window: (from, to) => `${from}–${to}`,
    levels: { yellow: 'Yellow', orange: 'Orange', red: 'Red' },
    source: (s) => `Source: ${s}`,
    partial: 'Some warnings could not be loaded.',
    unavailable: 'Official warnings are not available right now: check aemet.es before you set off.',
    loading: 'Checking official warnings…',
    stretch: (from, to, time) => `km ${from}–${to} · you pass around ${time}`,
    onRoute: (n) => (n === 1 ? '1 warning' : `${n} warnings`),
    kinds: {
      wind: 'Wind',
      rain: 'Rain',
      snow: 'Snow',
      storm: 'Thunderstorms',
      fog: 'Fog',
      heat: 'High temperatures',
      cold: 'Low temperatures',
      coastal: 'Coastal events',
      dust: 'Dust',
      other: 'Other',
    },
  },
  empty: {
    startTitle: 'Where to?',
    startBody: 'Pick a start and an end. We show up to three routes and the weather on each stretch as you pass.',
    noRouteTitle: 'No driving route',
    noRouteBody: 'There is no road route between those points. Make sure both are on land.',
    horizonTitle: 'Too far ahead',
    horizonBody: 'Hourly forecasts reach about 7 days. Pick an earlier departure.',
    offlineTitle: 'Offline',
    offlineBody: 'Showing the last saved route. Data may be out of date.',
    retry: 'Retry',
  },
  toast: {
    rateLimited: 'Too many requests in a row. Waiting a moment before retrying.',
    weatherDown: 'No weather data right now. The route is shown without risk.',
    copied: 'Link copied.',
    dismiss: 'Dismiss',
  },
  theme: { label: 'Theme', system: 'System', light: 'Light', dark: 'Dark' },
  lang: { label: 'Language', switchTo: 'Cambiar a español', short: 'ES' },
  units: { km: 'km', kmh: 'km/h' },
  explain: {
    rain: (mm) => `Rain of ${mm} mm/h`,
    crosswind: (cross, gust) => `Crosswind gusts of ${cross} km/h (gusts ${gust})`,
    gust: (gust) => `Gusts of ${gust} km/h`,
    snow: (cm) => `Snow of ${cm} cm/h`,
    ice: (temp) => `Possible ice: ${temp} °C with precipitation`,
    freezing: 'Freezing rain or drizzle',
    visibility: (m) => `Visibility of ${m} m`,
    storm: 'Thunderstorm',
    hail: 'Risk of severe thunderstorm or hail',
    at: (time, km) => `${time} · km ${km}`,
    partial: (factors) => `No ${factors} data on some stretches.`,
    beyondForecast: (km) =>
      `The hourly forecast does not reach the end of the trip: no data from km ${km}. Leave earlier or pick another day.`,
    unknown: 'No weather data for that time.',
    upTo: (km) => `up to ${km} km`,
  },
  status: {
    routingDownBody: 'It may be a brief outage of the public servers. Retry in a few seconds.',
    weatherPartial: 'Forecast missing for some stretches: they are shown without risk.',
    routing: 'Finding routes…',
    weather: 'Getting the forecast for each stretch…',
    points: (done, total) => `${done} of ${total} points`,
    noRoute: 'There is no driving route between those points.',
    routingDown: 'Routing servers are not responding. Try again in a moment.',
    weatherDown: 'No weather forecast right now: routes are shown without risk.',
    rateLimited: 'Too many requests in a row: wait a few seconds and retry.',
    retry: 'Retry',
    fallback: 'Using the backup routing server.',
    pickBoth: 'Pick a start and an end to compare routes.',
  },
  route: {
    label: (letter) => `Route ${letter}`,
    unnamed: 'Unnamed route',
    via: (km) => `${km} km`,
    worstPoint: (route, time) => `Worst point on route ${route} · ${time}`,
    worstShort: (route) => `Worst point · route ${route}`,
  },
  confidence: {
    low: 'Beyond 48 h the hourly forecast is only a guide: the detail can change.',
  },
  attribution:
    '© OpenStreetMap contributors · OpenFreeMap · Weather data by Open-Meteo.com (CC-BY 4.0) · Routing by Valhalla (FOSSGIS) / OSRM / OpenRouteService · Warnings: Source: AEMET · Meteoalarm (EUMETNET) · Places: Wikidata',
};
